import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Field'
import { formatAgo, shiftDays, today } from '@/lib/dates'
import { jobName } from '@/lib/jobs'
import { useCurrentStaff } from '@/features/auth/RequireStaff'
import { useRequestDraft, useUpdateCommunication, useUpdateDraft, whatsAppLink, type CommDraft, type Communication } from './queries'

const CHANNEL_LABEL: Record<Communication['channel'], string> = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  phone: 'Phone',
  other: 'Other',
}

/**
 * One conversation: who, about which job, what was said last, what to do, and
 * the drafts written for it. Nothing here sends a message.
 */
export function CommRow({ comm, showJob = true }: { comm: Communication; showJob?: boolean }) {
  const { staff } = useCurrentStaff()
  const update = useUpdateCommunication()
  const request = useRequestDraft()
  const [asking, setAsking] = useState(false)
  const [instructions, setInstructions] = useState('')

  const drafts = comm.drafts.filter((draft) => draft.status !== 'discarded' && draft.status !== 'sent')

  return (
    <li className="card px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="min-w-0 text-[length:var(--text-sm)]">
          <span className="font-medium">{comm.counterpart_name ?? comm.counterpart_address ?? 'Unknown'}</span>
          {showJob && comm.job ? (
            <Link to={`/weddings/${comm.job.id}`} className="display ml-2 text-[0.9375rem] hover:underline">
              {jobName(comm.job)}
            </Link>
          ) : null}
          {comm.subject ? <span className="ml-2 text-[length:var(--text-2xs)] text-taupe">{comm.subject}</span> : null}
        </p>
        <span className="flex shrink-0 items-baseline gap-3 text-[length:var(--text-2xs)] text-taupe">
          <span className="label">{CHANNEL_LABEL[comm.channel]}</span>
          <span>
            {comm.last_from === 'them' ? 'They wrote' : 'You wrote'} {formatAgo(comm.last_message_at)}
          </span>
        </span>
      </div>

      {comm.summary ? <p className="mt-1 text-[length:var(--text-sm)]">{comm.summary}</p> : null}
      {comm.next_step && comm.state === 'waiting_on_us' ? (
        <p className="mt-0.5 text-[length:var(--text-2xs)] text-amber">Next: {comm.next_step}</p>
      ) : null}

      {drafts.map((draft) => (
        <DraftBlock key={draft.id} draft={draft} />
      ))}

      <div className="mt-2 flex flex-wrap gap-1">
        {comm.state !== 'done' ? (
          <Button size="sm" variant="ghost" className="-ml-2.5" onClick={() => update.mutate({ id: comm.id, patch: { state: 'done', done_at: new Date().toISOString() } })}>
            Done
          </Button>
        ) : (
          <Button size="sm" variant="ghost" className="-ml-2.5" onClick={() => update.mutate({ id: comm.id, patch: { state: 'waiting_on_us', done_at: null } })}>
            Reopen
          </Button>
        )}
        {comm.state === 'waiting_on_us' ? (
          <Button size="sm" variant="ghost" onClick={() => update.mutate({ id: comm.id, patch: { snoozed_until: shiftDays(today(), 3) } })}>
            Later (3 days)
          </Button>
        ) : null}
        {!asking ? (
          <Button size="sm" variant="ghost" onClick={() => setAsking(true)}>
            Draft a reply
          </Button>
        ) : null}
      </div>

      {asking ? (
        <div className="mt-2 flex flex-col gap-2">
          <Textarea
            rows={2}
            value={instructions}
            placeholder="What should it say? e.g. Yes, 14 March is free; mention the 40% deposit. Leave empty for a suggested reply."
            onChange={(event) => setInstructions(event.target.value)}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              loading={request.isPending}
              onClick={async () => {
                await request.mutateAsync({ communication: comm, instructions, staffId: staff.id })
                setAsking(false)
                setInstructions('')
              }}
            >
              Ask for a draft
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAsking(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  )
}

function DraftBlock({ draft }: { draft: CommDraft }) {
  const update = useUpdateDraft()

  if (draft.status === 'requested') {
    return (
      <p className="mt-2 border-l-2 border-l-[var(--color-rule-strong)] pl-3 text-[length:var(--text-2xs)] text-taupe">
        Draft requested{draft.instructions ? `: “${draft.instructions}”` : ''}. Ready after the next check.
      </p>
    )
  }

  const wa = draft.channel === 'whatsapp' ? whatsAppLink(draft.to_address, draft.body) : null

  return (
    <div className="mt-2 border-l-2 border-l-[var(--border-hairline)] pl-3">
      <div className="mb-1 flex items-center gap-2">
        <Badge tone="amber">{draft.status === 'placed' ? 'Draft in your Gmail' : 'Draft'}</Badge>
      </div>
      <p className="text-[length:var(--text-sm)] whitespace-pre-wrap">{draft.body}</p>
      <div className="mt-2 flex flex-wrap gap-1">
        {wa ? (
          <a
            href={wa}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex h-7 items-center rounded-md bg-[var(--color-wash-strong)] px-2.5 text-[length:var(--text-xs)] font-medium hover:bg-[var(--color-rule)]"
          >
            Open in WhatsApp
          </a>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            void navigator.clipboard.writeText(draft.body ?? '').then(() => toast.success('Copied'))
          }}
        >
          Copy
        </Button>
        <Button size="sm" variant="ghost" onClick={() => update.mutate({ id: draft.id, patch: { status: 'sent' } })}>
          I sent it
        </Button>
        <Button size="sm" variant="ghost" onClick={() => update.mutate({ id: draft.id, patch: { status: 'discarded' } })}>
          Discard
        </Button>
      </div>
    </div>
  )
}
